import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const CampaignHistoryTab = () => {
    const [campaignHistory, setCampaignHistory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [showDetails, setShowDetails] = useState(false);

    useEffect(() => {
        fetchCampaignHistory();
    }, []);

    const fetchCampaignHistory = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/user-campaign-tracking/campaign-history');
            console.log('API Response:', response); // Debug için
            
            // Response yapısını kontrol et
            if (response && response.success) {
                setCampaignHistory(response.data);
            } else if (response && response.data && response.data.success) {
                setCampaignHistory(response.data.data);
            } else {
                console.error('Beklenmeyen API response yapısı:', response);
                setError('Kampanya geçmişi verisi alınamadı.');
            }
        } catch (error) {
            console.error('Kampanya geçmişi yüklenirken hata:', error);
            setError('Kampanya geçmişi yüklenirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    const fetchCampaignDetails = async (campaignId) => {
        try {
            const response = await api.get(`/api/user-campaign-tracking/campaign-details/${campaignId}`);
            console.log('Campaign Details Response:', response); // Debug için
            
            if (response && response.success) {
                setSelectedCampaign(response.data);
                setShowDetails(true);
            } else if (response && response.data && response.data.success) {
                setSelectedCampaign(response.data.data);
                setShowDetails(true);
            } else {
                console.error('Kampanya detayları alınamadı:', response);
            }
        } catch (error) {
            console.error('Kampanya detayları yüklenirken hata:', error);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('tr-TR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY'
        }).format(price);
    };

    const getCampaignTypeText = (type) => {
        const types = {
            'percentage': 'Yüzde İndirim',
            'fixed': 'Sabit İndirim',
            'category': 'Kategori İndirimi',
            'package': 'Paket İndirimi',
            'special_day': 'Özel Gün İndirimi',
            'flash_sale': 'Flaş İndirim'
        };
        return types[type] || type;
    };

    const getCampaignTypeBadgeColor = (type) => {
        const colors = {
            'percentage': 'bg-pink-100 text-pink-800',
            'fixed': 'bg-blue-100 text-blue-800',
            'category': 'bg-purple-100 text-purple-800',
            'package': 'bg-green-100 text-green-800',
            'special_day': 'bg-yellow-100 text-yellow-800',
            'flash_sale': 'bg-red-100 text-red-800'
        };
        return colors[type] || 'bg-gray-100 text-gray-800';
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
                <span className="ml-3 text-gray-600">Kampanya geçmişiniz yükleniyor...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-12">
                <div className="text-red-500 mb-4">
                    <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-lg font-medium">{error}</p>
                </div>
                <button 
                    onClick={fetchCampaignHistory}
                    className="bg-pink-500 text-white px-6 py-2 rounded-lg hover:bg-pink-600 transition-colors"
                >
                    Tekrar Dene
                </button>
            </div>
        );
    }

    if (!campaignHistory || !campaignHistory.campaignUsage || campaignHistory.campaignUsage.length === 0) {
        return (
            <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                    <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    <h3 className="text-xl font-medium text-gray-600 mb-2">Henüz Kampanya Kullanımınız Yok</h3>
                    <p className="text-gray-500">Alışveriş yaparken kampanyalardan yararlanın ve burada geçmişinizi görün!</p>
                </div>
            </div>
        );
    }

    const { statistics } = campaignHistory;

    return (
        <div className="space-y-6">
            {/* İstatistikler */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-gradient-to-r from-pink-500 to-purple-600 rounded-xl p-6 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-pink-100 text-sm font-medium">Toplam Tasarruf</p>
                            <p className="text-2xl font-bold">{formatPrice(statistics.totalSavings)}</p>
                        </div>
                        <div className="bg-white bg-opacity-20 rounded-lg p-3">
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                            </svg>
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl p-6 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-blue-100 text-sm font-medium">Kullanılan Kampanya</p>
                            <p className="text-2xl font-bold">{statistics.totalCampaignsUsed}</p>
                        </div>
                        <div className="bg-white bg-opacity-20 rounded-lg p-3">
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/>
                            </svg>
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-r from-green-500 to-teal-600 rounded-xl p-6 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-green-100 text-sm font-medium">En Çok Kullanılan</p>
                            <p className="text-lg font-bold">{getCampaignTypeText(statistics.mostUsedCampaignType)}</p>
                        </div>
                        <div className="bg-white bg-opacity-20 rounded-lg p-3">
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

            {/* Kampanya Geçmişi Listesi */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="p-6 border-b border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900">Kampanya Kullanım Geçmişi</h3>
                    <p className="text-sm text-gray-600 mt-1">Kullandığınız kampanyalar ve tasarruf detayları</p>
                </div>

                <div className="divide-y divide-gray-100">
                    {campaignHistory.campaignUsage.map((usage, index) => (
                        <div key={index} className="p-6 hover:bg-gray-50 transition-colors">
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <h4 className="font-medium text-gray-900">
                                            {usage.campaigns?.name || 'Kampanya'}
                                        </h4>
                                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCampaignTypeBadgeColor(usage.campaigns?.campaign_type)}`}>
                                            {getCampaignTypeText(usage.campaigns?.campaign_type)}
                                        </span>
                                        {usage.campaigns?.badge_text && (
                                            <span 
                                                className="px-2 py-1 text-xs font-medium rounded-full text-white"
                                                style={{ backgroundColor: usage.campaigns.badge_color || '#ef4444' }}
                                            >
                                                {usage.campaigns.badge_text}
                                            </span>
                                        )}
                                    </div>
                                    
                                    <p className="text-sm text-gray-600 mb-3">
                                        {usage.campaigns?.description || 'Kampanya açıklaması mevcut değil'}
                                    </p>

                                    <div className="flex items-center gap-6 text-sm text-gray-500">
                                        <span className="flex items-center gap-1">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            {formatDate(usage.used_at)}
                                        </span>
                                    </div>
                                </div>

                                <div className="text-right ml-4">
                                    <div className="text-lg font-semibold text-green-600 mb-1">
                                        -{formatPrice(usage.discount_amount)}
                                    </div>
                                    <button 
                                        onClick={() => fetchCampaignDetails(usage.campaign_id)}
                                        className="text-sm text-pink-600 hover:text-pink-700 font-medium"
                                    >
                                        Detayları Gör
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Kampanya Detay Modal */}
            {showDetails && selectedCampaign && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-100">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xl font-semibold text-gray-900">Kampanya Detayları</h3>
                                <button 
                                    onClick={() => setShowDetails(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="mb-6">
                                <h4 className="text-lg font-medium text-gray-900 mb-2">
                                    {selectedCampaign.campaign.name}
                                </h4>
                                <p className="text-gray-600 mb-4">
                                    {selectedCampaign.campaign.description}
                                </p>
                                
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-gray-500">Kampanya Türü:</span>
                                        <span className="ml-2 font-medium">
                                            {getCampaignTypeText(selectedCampaign.campaign.campaign_type)}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">İndirim Türü:</span>
                                        <span className="ml-2 font-medium">
                                            {selectedCampaign.campaign.discount_type === 'percentage' ? 'Yüzde' : 'Sabit Tutar'}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">İndirim Değeri:</span>
                                        <span className="ml-2 font-medium">
                                            {selectedCampaign.campaign.discount_type === 'percentage' 
                                                ? `%${selectedCampaign.campaign.discount_value}`
                                                : formatPrice(selectedCampaign.campaign.discount_value)
                                            }
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {selectedCampaign.userUsage && selectedCampaign.userUsage.length > 0 && (
                                <div>
                                    <h5 className="font-medium text-gray-900 mb-3">Kullanım Geçmişiniz</h5>
                                    <div className="space-y-3">
                                        {selectedCampaign.userUsage.map((usage, index) => (
                                            <div key={index} className="bg-gray-50 rounded-lg p-4">
                                                <div className="flex justify-between items-center">
                                                    <div>
                                                        <p className="text-sm text-gray-600">
                                                            {formatDate(usage.used_at)}
                                                        </p>
                
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="font-medium text-green-600">
                                                            -{formatPrice(usage.discount_amount)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CampaignHistoryTab;